/**
 * OpCode create templates — valid seed shapes for jsonOpcodeService.createJSONOpcode.
 */

/** Minimal step — BeX Server requires opcode.steps.length ≥ 1 on create. */
export const BLANK_PLACEHOLDER_STEP = {
    id: 'step_1',
    kind: 'memory'
};

/** Minimal seed for "New opcode" — empty I/O; one placeholder step for API validation. */
export const BLANK_OPCODE_SEED = {
    title: 'New OpCode',
    description: '',
    version: '0.1.0',
    inputs: [],
    outputs: [],
    steps: [BLANK_PLACEHOLDER_STEP]
};

const BASE_INPUT = {
    name: 'query',
    type: 'string',
    required: true,
    description: 'User question or request',
    default: null
};

const BASE_OUTPUT = {
    name: 'result',
    type: 'string',
    description: 'Final answer',
    oel_expression: '@step_2.output.results'
};

export const OPCODE_TEMPLATES = [
    {
        id: 'rag-answer',
        title: 'Answer from knowledge',
        description: 'Search your knowledge base, then ask AI to answer using the results.',
        seed: {
            title: 'Answer from knowledge',
            description: 'Retrieve relevant passages, then generate an answer.',
            version: '0.1.0',
            inputs: [BASE_INPUT],
            outputs: [{
                ...BASE_OUTPUT,
                oel_expression: '@step_2.output.results'
            }],
            steps: [
                {
                    id: 'step_1',
                    kind: 'rag',
                    description: 'Search knowledge',
                    operation: 'basic_search',
                    inputs: {
                        collection_id: '',
                        query: '@input.query',
                        top_k: 5
                    }
                },
                {
                    id: 'step_2',
                    kind: 'llm',
                    description: 'Generate answer',
                    unique_name: '',
                    model_type: '',
                    output_type: 'string',
                    inputs: { query: '@step_1.output.chunks' }
                }
            ]
        }
    },
    {
        id: 'api-summarize',
        title: 'Call API and summarize',
        description: 'Fetch data from an API, then summarize with AI.',
        seed: {
            title: 'API summarize',
            description: 'Call an API and summarize the response.',
            version: '0.1.0',
            inputs: [BASE_INPUT],
            outputs: [{
                name: 'result',
                type: 'string',
                description: 'Summary',
                oel_expression: '@step_2.output.results'
            }],
            steps: [
                {
                    id: 'step_1',
                    kind: 'api',
                    description: 'Call API',
                    api_id: '',
                    inputs: {}
                },
                {
                    id: 'step_2',
                    kind: 'llm',
                    description: 'Summarize response',
                    unique_name: '',
                    model_type: '',
                    output_type: 'string',
                    inputs: { query: '@step_1.output.body' }
                }
            ]
        }
    },
    {
        id: 'llm-only',
        title: 'Ask AI',
        description: 'Single AI step for straightforward tasks.',
        seed: {
            title: 'Ask AI',
            description: 'Send input to an instruction and return the response.',
            version: '0.1.0',
            inputs: [BASE_INPUT],
            outputs: [{
                name: 'result',
                type: 'string',
                description: 'AI response',
                oel_expression: '@step_1.output.results'
            }],
            steps: [
                {
                    id: 'step_1',
                    kind: 'llm',
                    description: 'Ask AI',
                    unique_name: '',
                    model_type: '',
                    output_type: 'string',
                    inputs: { query: '@input.query' }
                }
            ]
        }
    },
    {
        id: 'blank',
        title: 'Blank procedure',
        description: 'Start empty and add steps yourself.',
        seed: { ...BLANK_OPCODE_SEED }
    }
];

export function getTemplateById(id) {
    return OPCODE_TEMPLATES.find((t) => t.id === id) || OPCODE_TEMPLATES.find((t) => t.id === 'blank');
}
